import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';

// ── Désactiver console en production ─────────────────────────────
if (process.env.NODE_ENV === 'production') {
  console.log  = () => {};
  console.warn = () => {};
  console.info = () => {};
  // Garder console.error pour les vrais bugs en prod
}

// ── Vérification de version au démarrage ─────────────────────────
// BUILD_ID est injecté par le script deploy via REACT_APP_BUILD_ID=<timestamp>.
// À chaque nouveau deploy, le BUILD_ID change → l'app détecte la différence,
// déconnecte l'utilisateur et recharge la page pour charger le nouveau bundle.

const BUILD_ID     = process.env.REACT_APP_BUILD_ID || 'dev';
const BUILD_ID_KEY = 'cmob_build_id';
const SESSION_KEY  = 'cmob_supabase_session';

const checkBuildVersion = () => {
  try {
    const storedBuildId = localStorage.getItem(BUILD_ID_KEY);

    if (storedBuildId && storedBuildId !== BUILD_ID) {
      console.error(`[Deploy] Nouvelle version détectée (${storedBuildId} → ${BUILD_ID}) — déconnexion forcée`);

      // Logout Supabase silencieux (best-effort, pas bloquant)
      try {
        const raw = localStorage.getItem(SESSION_KEY);
        const token = raw ? JSON.parse(raw)?.access_token : null;
        const SUPABASE_URL  = process.env.REACT_APP_SUPABASE_URL  || '';
        const SUPABASE_ANON = process.env.REACT_APP_SUPABASE_ANON || '';
        if (token && SUPABASE_URL) {
          fetch(`${SUPABASE_URL}/auth/v1/logout`, {
            method: 'POST',
            headers: { apikey: SUPABASE_ANON, Authorization: `Bearer ${token}` },
          }).catch(() => {});
        }
      } catch {}

      // Purger tout le localStorage sauf le BUILD_ID qu'on va réécrire
      const allKeys = Object.keys(localStorage);
      allKeys.forEach(k => localStorage.removeItem(k));
      localStorage.setItem(BUILD_ID_KEY, BUILD_ID);

      // Reload avec cache-busting (force rechargement du nouveau bundle JS)
      window.location.replace(window.location.pathname + '?v=' + BUILD_ID);
      return true; // indiquer qu'un reload est en cours
    }

    // Première visite ou même version → juste stocker le BUILD_ID
    localStorage.setItem(BUILD_ID_KEY, BUILD_ID);
    return false;
  } catch {
    return false;
  }
};

// ── Error Boundary global ─────────────────────────────────────────
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    console.error('ErrorBoundary:', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          minHeight: '100vh', display: 'flex', alignItems: 'center',
          justifyContent: 'center', background: '#f8fafc', fontFamily: 'sans-serif',
        }}>
          <div style={{
            background: '#fff', borderRadius: 16, padding: '40px 48px',
            boxShadow: '0 8px 40px rgba(0,48,135,0.12)', textAlign: 'center',
            maxWidth: 480,
          }}>
            <div style={{ fontSize: '3rem', marginBottom: 16 }}>⚠️</div>
            <h2 style={{ color: '#1a2f6e', marginBottom: 8 }}>Une erreur est survenue</h2>
            <p style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: 24 }}>
              L'application a rencontré un problème inattendu.
              Vos données sont sauvegardées.
            </p>
            <button
              onClick={() => window.location.reload()}
              style={{
                background: '#1a2f6e', color: '#fff', border: 'none',
                borderRadius: 8, padding: '10px 24px', cursor: 'pointer',
                fontWeight: 700, fontSize: '0.9rem',
              }}
            >
              🔄 Recharger l'application
            </button>
            {process.env.NODE_ENV !== 'production' && this.state.error && (
              <details style={{ marginTop: 20, textAlign: 'left' }}>
                <summary style={{ cursor: 'pointer', color: '#64748b', fontSize: '0.8rem' }}>
                  Détail de l'erreur (dev)
                </summary>
                <pre style={{
                  fontSize: '0.72rem', background: '#f1f5f9', padding: 12,
                  borderRadius: 6, overflow: 'auto', marginTop: 8,
                }}>
                  {this.state.error.toString()}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Vérifier la version AVANT de monter l'app.
// Si un nouveau deploy est détecté, checkBuildVersion() recharge la page
// et on ne monte jamais l'app (évite un flash de l'ancienne UI).
if (!checkBuildVersion()) {
  const root = ReactDOM.createRoot(document.getElementById('root'));
  root.render(
    <React.StrictMode>
      <ErrorBoundary>
        <App />
      </ErrorBoundary>
    </React.StrictMode>
  );
  reportWebVitals();
}